import type { Item, Stage, TextSlice } from '@mystiq/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, sameOrigin, type Api } from '../api/client.ts';
import { keys, useItem, useWorkspaceId, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi, useSession } from '../auth/session.tsx';
import { CommandBar } from '../components/CommandBar.tsx';
import { CommentsProvider, CommentsSection, useComments } from '../components/Comments.tsx';
import { byline, Details, FolderCrumb, ItemActions, Processing, RelatedSection, TitleField, TrashedNotice } from '../components/ItemParts.tsx';
import { RecordingPlayer, TimedTranscript } from '../components/Player.tsx';
import { ReadAloudControl } from '../components/ReadAloud.tsx';
import { SiteView } from '../components/SiteView.tsx';
import { EmptyState, ErrorLine } from '../components/ui.tsx';
import { AutosaveController, SaveFailure, type AutosaveState } from '../editor/autosave.ts';
import { draftKey, localDraftStore, type StoredDraft } from '../editor/drafts.ts';
import { NoteBody } from '../editor/NoteEditor.tsx';
import { Comment, Download, File as FileIcon, ICON, Site } from '../icons.ts';
import { clockTime, formatBytes, formatDuration, kindLabel } from '../lib/format.ts';

export function ItemPage() {
  const ws = useWorkspaceId();
  const { itemId = '' } = useParams();
  const item = useItem(ws, itemId);
  if (item.isError) {
    const gone = item.error instanceof ApiError && item.error.code === 'NOT_FOUND_OR_DENIED';
    return <EmptyState icon={FileIcon} title={gone ? 'This item does not exist or is not in this workspace.' : 'Could not load this item.'} />;
  }
  if (!item.data) return <p className="meta">Loading…</p>;
  if (item.data.lifecycle === 'trashed') return <TrashedNotice ws={ws} item={item.data} />;
  if (item.data.kind === 'site') return <SiteView key={item.data.id} ws={ws} item={item.data} />;
  // Parts of the page (editor, player) can say what a new comment points at.
  return (
    <CommentsProvider key={item.data.id}>
      {item.data.kind === 'note' ? (
        <NoteView ws={ws} item={item.data} />
      ) : isRecording(item.data) ? (
        <RecordingView ws={ws} item={item.data} />
      ) : item.data.kind === 'file' ? (
        <FileView ws={ws} item={item.data} />
      ) : (
        <TextItemView ws={ws} item={item.data} />
      )}
    </CommentsProvider>
  );
}

/** Audio and video files: played, and transcribed rather than read. */
function isRecording(item: Item): boolean {
  return item.kind === 'file' && (item.capture !== null || /^(audio|video)\//.test(item.mimeType ?? '') || item.mimeType === 'application/ogg');
}

function NoteView({ ws, item }: { ws: string; item: Item }) {
  return (
    <div className="page-narrow">
      <FolderCrumb ws={ws} item={item} />
      <TitleField key={`title-${item.id}`} ws={ws} item={item} placeholder="Untitled note" />
      <p className="meta">{byline(item)}</p>
      <div className="content">
        <NoteBody key={item.id} ws={ws} item={item} />
      </div>
      <CommentsSection ws={ws} item={item} hint="Select words in the note and choose Comment on selection to point at them, or comment on the whole note." />
      <Details key={`details-${item.id}`} ws={ws} item={item} />
      <Processing ws={ws} item={item} />
      <RelatedSection ws={ws} itemId={item.id} />
      <ItemActions ws={ws} item={item} />
    </div>
  );
}

function FileView({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [opening, setOpening] = useState(false);
  // One key per attempt: a retry after a lost answer repeats it; the next attempt is a new request.
  const openKey = useRef<string | null>(null);
  const extraction = item.stages.find((stage) => stage.kind === 'extract_text');
  const text = useQuery({
    queryKey: keys.text(ws, item.id),
    queryFn: () => api.readText(ws, item.id),
    enabled: (extraction?.status === 'succeeded' || extraction?.status === 'partial') && !extraction.notes.includes('No readable text was found.'),
  });
  const available = item.storageState === 'available';
  const readable = Boolean(text.data && text.data.totalChars > 0);
  return (
    <div className="page">
      <FolderCrumb ws={ws} item={item} />
      <TitleField key={`title-${item.id}`} ws={ws} item={item} placeholder="Untitled file" />
      <p className="meta">
        {[kindLabel(item), item.mimeType, formatBytes(item.sizeBytes)].filter(Boolean).join(' · ')} · {byline(item)}
      </p>
      <div className="toolbar">
        <button
          className="button button-primary"
          disabled={!available}
          onClick={async () => {
            try {
              const link = await api.downloadLink(ws, item.id);
              window.location.assign(sameOrigin(link.url));
            } catch (caught) {
              setError(caught);
            }
          }}
        >
          <Download size={ICON.nav} stroke={ICON.stroke} />
          Download
        </button>
        {available && item.mimeType === 'text/html' && item.permittedActions.includes('move') && (
          <button
            className="button"
            disabled={opening}
            onClick={async () => {
              setOpening(true);
              try {
                openKey.current ??= crypto.randomUUID();
                const operation = await api.createSite(ws, { htmlFileItemId: item.id, idempotencyKey: openKey.current });
                openKey.current = null;
                if (typeof operation.result.outputItemId === 'string') navigate(`/w/${ws}/items/${operation.result.outputItemId}`);
              } catch (caught) {
                setError(caught);
              } finally {
                setOpening(false);
              }
            }}
          >
            <Site size={ICON.nav} stroke={ICON.stroke} />
            Open as a Site
          </button>
        )}
        {!available && (
          <span className="meta">
            <span className="state">{item.storageState === 'failed' ? 'Upload failed.' : 'Still uploading.'}</span>
          </span>
        )}
      </div>
      <ErrorLine error={error} />
      {item.permittedActions.includes('move') && (
        <CommandBar ws={ws} placeholder="Ask Mystiq about this file, e.g. tag it, rename it, write a summary" context={() => ({ itemIds: [item.id] })} />
      )}
      <Processing ws={ws} item={item} />
      {text.data && (
        <section className="section" aria-labelledby="text-heading">
          <h2 className="section-heading" id="text-heading">Text</h2>
          <div className="stack">
            <p className="meta">
              Extracted text is its own item: it stays available even if this file goes to Trash.{' '}
              <Link to={`/w/${ws}/items/${text.data.itemId}`}>Open the text</Link>
            </p>
            {readable && <ReadAloudControl ws={ws} itemId={item.id} />}
            <TextPreview slice={text.data} limit={4000} />
          </div>
        </section>
      )}
      <CommentsSection ws={ws} item={item} hint="Comments here are about the whole file. To point at its words, comment on its text." />
      <Details key={`details-${item.id}`} ws={ws} item={item} />
      <RelatedSection ws={ws} itemId={item.id} />
      <ItemActions ws={ws} item={item} />
    </div>
  );
}

/** The start of a file's text, labelled by page, slide or sheet when the reader recorded them. */
function TextPreview({ slice, limit }: { slice: TextSlice; limit: number }) {
  const more = slice.totalChars > limit ? '…' : '';
  const shown = slice.sections.filter((section) => section.start < limit);
  if (!shown.length) return <div className="text-view">{slice.text.slice(0, limit)}{more}</div>;
  return (
    <div className="text-view">
      {shown.map((section, index) => (
        <div key={section.label + section.start}>
          <p className="field-label">{section.label}</p>
          {slice.text.slice(section.start, Math.min(section.end, limit))}
          {index === shown.length - 1 ? more : ''}
        </div>
      ))}
    </div>
  );
}

/**
 * A recording: played here, its transcript beside it (each passage plays the
 * recording from where it was heard), and what was written from it. The
 * recording, its transcript and the writing are separate items.
 */
function RecordingView({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const comments = useComments();
  const [error, setError] = useState<unknown>(null);
  const media = useRef<HTMLMediaElement | null>(null);
  const [playingMs, setPlayingMs] = useState(0);
  const transcription = item.stages.find((stage) => stage.kind === 'transcribe');
  const transcriptId = transcription && (transcription.status === 'succeeded' || transcription.status === 'partial') ? transcription.outputItemId : null;
  const text = useQuery({ queryKey: [...keys.text(ws, item.id), 'whole'], queryFn: () => readWholeText(api, ws, item.id), enabled: Boolean(transcriptId) });
  const available = item.storageState === 'available';
  const seek = (ms: number) => {
    const element = media.current;
    if (!element) return;
    element.currentTime = ms / 1000;
    void element.play().catch(() => undefined);
  };
  return (
    <div className="page">
      <FolderCrumb ws={ws} item={item} />
      <TitleField key={`title-${item.id}`} ws={ws} item={item} placeholder="Untitled recording" />
      <p className="meta">
        {[kindLabel(item), item.media ? formatDuration(item.media.durationMs) : null, formatBytes(item.sizeBytes)].filter(Boolean).join(' · ')} · {byline(item)}
      </p>
      <div className="content stack">
        {available ? (
          <RecordingPlayer ws={ws} item={item} media={media} onTime={setPlayingMs} />
        ) : (
          <p className="meta">
            <span className="state">{item.storageState === 'failed' ? 'Upload failed.' : 'Still uploading.'}</span>
          </p>
        )}
        <div className="toolbar" style={{ marginTop: 0 }}>
          <button
            className="button"
            disabled={!available}
            onClick={async () => {
              try {
                const link = await api.downloadLink(ws, item.id);
                window.location.assign(sameOrigin(link.url));
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            <Download size={ICON.nav} stroke={ICON.stroke} />
            Download
          </button>
          {available && item.media && item.permittedActions.includes('comment') && (
            <button
              className="button"
              onClick={() => {
                const at = Math.min(Math.round(playingMs), item.media!.durationMs);
                comments?.propose({ anchor: { kind: 'time', startMs: at }, label: `At ${clockTime(at)}` });
              }}
            >
              <Comment size={ICON.nav} stroke={ICON.stroke} />
              Comment at {clockTime(playingMs)}
            </button>
          )}
        </div>
        <ErrorLine error={error} />
      </div>
      {item.permittedActions.includes('move') && (
        <CommandBar ws={ws} placeholder="Ask Mystiq about this recording, e.g. tag it, rename it, write a summary" context={() => ({ itemIds: [item.id] })} />
      )}
      <section className="section" aria-labelledby="transcript-heading">
        <h2 className="section-heading" id="transcript-heading">Transcript</h2>
        <div className="stack">
          <TranscriptState ws={ws} item={item} stage={transcription} />
          {text.data && (
            <>
              <TimedTranscript slice={text.data} playingMs={playingMs} onSeek={seek} />
              <p className="meta">
                {text.data.timings.length ? 'Choose a time to play the recording from there. ' : 'This transcript was edited, so its passages no longer have times. '}
                The transcript is its own item: <Link to={`/w/${ws}/items/${text.data.itemId}`}>open it</Link> to correct it. The recording never changes.
              </p>
            </>
          )}
          {transcriptId && item.capture && <WrittenFromRecording ws={ws} item={item} transcriptId={transcriptId} />}
        </div>
      </section>
      <CommentsSection ws={ws} item={item} hint="Play the recording and choose Comment at a moment to point at that time, or comment on the whole recording." />
      <Processing ws={ws} item={item} />
      <Details key={`details-${item.id}`} ws={ws} item={item} />
      <RelatedSection ws={ws} itemId={item.id} />
      <ItemActions ws={ws} item={item} />
    </div>
  );
}

/** All of a text, read slice by slice (a long meeting's transcript is more than one slice), with every timing once. */
async function readWholeText(api: Api, ws: string, itemId: string): Promise<TextSlice> {
  const first = await api.readText(ws, itemId);
  let slice = first;
  let text = first.text;
  const timings = [...first.timings];
  const seen = new Set(timings.map((timing) => `${timing.start}:${timing.startMs}`));
  for (let more = 0; slice.nextOffset !== null && more < 50; more++) {
    slice = await api.readText(ws, itemId, slice.nextOffset);
    text += slice.text;
    for (const timing of slice.timings) {
      const key = `${timing.start}:${timing.startMs}`;
      if (seen.has(key)) continue;
      seen.add(key);
      timings.push(timing);
    }
  }
  return { ...first, text, timings, nextOffset: slice.nextOffset };
}

/** Where transcription is, in plain words, with Retry when it can be tried again. */
function TranscriptState({ ws, item, stage }: { ws: string; item: Item; stage: Stage | undefined }) {
  const api = useApi();
  const retry = useWorkspaceMutation(ws, (operationId: string) => api.retryOperation(ws, operationId));
  if (!stage) return item.storageState === 'available' ? null : <p className="meta">Transcribed once the upload finishes.</p>;
  const canRetry = item.permittedActions.includes('move') && (stage.status === 'failed' || stage.status === 'cancelled');
  let line;
  switch (stage.status) {
    case 'queued':
    case 'running':
      line = (
        <>
          <span className="state">Transcribing.</span> {stage.errorCode ? `Trying again after a problem: ${stage.errorMessage ?? ''}` : 'The words appear here when they are ready; a long recording takes a few minutes.'}
        </>
      );
      break;
    case 'failed':
    case 'cancelled':
    case 'unsupported':
      line = (
        <>
          <span className="state">Not transcribed.</span> {stage.errorMessage ?? ''}
        </>
      );
      break;
    default:
      if (stage.status === 'partial') {
        line = (
          <>
            <span className="state">Transcribed in part.</span> {stage.notes.join(' ')}
          </>
        );
      } else if (!stage.outputItemId) {
        line = <span className="state">{stage.notes.join(' ') || 'No speech was heard.'}</span>;
      } else if (stage.notes.length) {
        line = <span className="meta">{stage.notes.join(' ')}</span>;
      } else {
        return null;
      }
  }
  return (
    <div className="stack">
      <p role="status">{line}</p>
      {canRetry && (
        <div>
          <button className="button button-inline" disabled={retry.isPending} onClick={() => retry.mutate(stage.operationId)}>
            Retry
          </button>
        </div>
      )}
      <ErrorLine error={retry.error} />
    </div>
  );
}

/** The cleaned transcript of a voice note, or a meeting's notes: written from the transcript, an item of its own. */
function WrittenFromRecording({ ws, item, transcriptId }: { ws: string; item: Item; transcriptId: string }) {
  const api = useApi();
  const purpose = item.capture === 'meeting' ? 'meeting_notes' : 'clean_transcript';
  const runs = useQuery({ queryKey: [...keys.item(ws, transcriptId), 'operations'], queryFn: () => api.itemOperations(ws, transcriptId) });
  const retry = useWorkspaceMutation(ws, (operationId: string) => api.retryOperation(ws, operationId));
  const run = runs.data?.find((operation) => operation.kind === 'transform' && operation.purpose === purpose);
  if (!run) return null;
  const name = purpose === 'meeting_notes' ? 'meeting notes' : 'clean transcript';
  const output = typeof run.result.outputItemId === 'string' ? run.result.outputItemId : null;
  return (
    <div className="stack">
      {(run.status === 'queued' || run.status === 'running') && (
        <p role="status">
          <span className="state">{purpose === 'meeting_notes' ? 'Writing the meeting notes.' : 'Cleaning up the transcript.'}</span> It appears here when it is ready.
        </p>
      )}
      {(run.status === 'succeeded' || run.status === 'partial') && output && (
        <p>
          <Link to={`/w/${ws}/items/${output}`}>Open the {name}</Link>
          <span className="meta"> · written by AI from the transcript; it is a note of its own{run.status === 'partial' ? ', made from part of the transcript' : ''}.</span>
        </p>
      )}
      {(run.status === 'failed' || run.status === 'cancelled') && (
        <>
          <p role="status">
            <span className="state">The {name} {run.status === 'cancelled' ? 'was stopped' : 'were not written'}.</span> {run.errorMessage ?? ''}
          </p>
          {item.permittedActions.includes('move') && (
            <div>
              <button className="button button-inline" disabled={retry.isPending} onClick={() => retry.mutate(run.id)}>
                Retry
              </button>
            </div>
          )}
          <ErrorLine error={retry.error} />
        </>
      )}
    </div>
  );
}

function TextItemView({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const text = useQuery({ queryKey: keys.text(ws, item.id), queryFn: () => api.readText(ws, item.id) });
  return (
    <div className="page-narrow">
      <FolderCrumb ws={ws} item={item} />
      <TitleField key={`title-${item.id}`} ws={ws} item={item} placeholder="Untitled text" />
      <p className="meta">{byline(item)}</p>
      <div className="content">
        {text.data ? <TextEditor key={item.id} ws={ws} item={item} revisionId={text.data.revisionId} initial={text.data.text} complete={text.data.nextOffset === null} /> : <p className="meta">Loading…</p>}
      </div>
      <CommentsSection ws={ws} item={item} hint="Select words in the text and choose Comment on selection to point at them, or comment on the whole text." />
      <Details key={`details-${item.id}`} ws={ws} item={item} />
      <Processing ws={ws} item={item} />
      <RelatedSection ws={ws} itemId={item.id} />
      <ItemActions ws={ws} item={item} />
    </div>
  );
}

function TextEditor({ ws, item, revisionId, initial, complete }: { ws: string; item: Item; revisionId: string; initial: string; complete: boolean }) {
  const api = useApi();
  const session = useSession();
  const client = useQueryClient();
  const comments = useComments();
  const [commentProblem, setCommentProblem] = useState<string | null>(null);
  const editable = complete && item.permittedActions.includes('edit');
  const drafts = useMemo(() => localDraftStore<string>(draftKey(session.userKey, item.id)), [session.userKey, item.id]);
  const [value, setValue] = useState(initial);
  const [state, setState] = useState<AutosaveState>({ status: 'saved', revisionId });
  const [recovery, setRecovery] = useState<StoredDraft<string> | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const controller = useRef<AutosaveController<string> | null>(null);
  if (!controller.current) {
    controller.current = new AutosaveController<string>({
      baseRevisionId: revisionId,
      draft: drafts,
      onChange: setState,
      save: async (base, body, key) => {
        try {
          const outcome = await api.editText(ws, item.id, base, body, key);
          if (outcome.status === 'conflict') return { status: 'conflict', currentRevisionId: outcome.currentRevisionId };
          // Keep the cache on the saved revision so reopening this item starts from it.
          client.setQueryData(keys.text(ws, item.id), (old: { revisionId: string; text: string } | undefined) =>
            old ? { ...old, revisionId: outcome.revisionId, text: body, totalChars: body.length, nextOffset: null } : old,
          );
          return { status: 'saved', revisionId: outcome.revisionId };
        } catch (error) {
          const retryable = !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
          throw new SaveFailure(retryable ? 'Could not reach Mystiq.' : (error as Error).message, retryable);
        }
      },
    });
  }
  // Newer saved text from elsewhere: load it only when nothing local is unsaved.
  useEffect(() => {
    if (controller.current!.acceptRemote(revisionId)) setValue(initial);
  }, [revisionId, initial]);

  // Recover an unsent draft from this browser (same person only).
  useEffect(() => {
    const draft = drafts.read();
    if (!draft || !editable || draft.doc === initial) return;
    if (draft.base === revisionId) {
      setValue(draft.doc);
      controller.current!.edit(draft.doc);
    } else {
      setRecovery(draft);
    }
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save before leaving: on navigation (unmount) and when the tab closes.
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      drafts.flush();
      if (controller.current!.hasUnsavedChanges) {
        void controller.current!.flush();
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      drafts.flush();
      void controller.current!.flush();
    };
  }, [drafts]);

  return (
    <div className="stack">
      {value.trim() && (
        <ReadAloudControl
          ws={ws}
          itemId={item.id}
          range={() => {
            const element = area.current;
            return element && element.selectionStart !== element.selectionEnd ? { start: element.selectionStart, end: element.selectionEnd } : null;
          }}
          beforeStart={() => controller.current!.flush()}
        />
      )}
      {complete && item.permittedActions.includes('comment') && comments && (
        <div className="row-actions">
          <button
            className="button button-inline"
            onClick={async () => {
              const element = area.current;
              if (!element || element.selectionStart === element.selectionEnd) return setCommentProblem('Select some words first.');
              const range = { start: element.selectionStart, end: element.selectionEnd };
              // A comment points at saved text, so anything typed is saved first.
              await controller.current!.flush();
              if (controller.current!.hasUnsavedChanges) return setCommentProblem('Save the text first: some changes are not saved yet.');
              setCommentProblem(null);
              comments.propose({ anchor: { kind: 'text', ...range }, revisionId: controller.current!.baseRevisionId, label: element.value.slice(range.start, range.end) });
            }}
          >
            <Comment size={ICON.inline} stroke={ICON.stroke} />
            Comment on selection
          </button>
          {commentProblem && <span className="meta">{commentProblem}</span>}
        </div>
      )}
      <p className="save-status" role="status" data-save-state={state.status}>
        {!editable
          ? complete
            ? 'Read-only: your role can view this text.'
            : 'This text is too long to edit here; it can be read and searched.'
          : state.status === 'saved'
            ? 'Saved'
            : state.status === 'saving'
              ? 'Saving…'
              : state.status === 'pending'
                ? 'Unsaved changes'
                : state.status === 'conflict'
                  ? 'Not saved: changed elsewhere'
                  : `Save failed. ${state.message}`}
      </p>
      {recovery && (
        <div className="conflict" role="alert">
          <p>
            <span className="state">Unsaved changes found</span> from {new Date(recovery.at).toLocaleString()}, made before this text changed elsewhere.
          </p>
          <div className="row-actions">
            <button
              className="button"
              onClick={() => {
                setValue(recovery.doc);
                controller.current!.edit(recovery.doc);
                controller.current!.keepMine(revisionId);
                setRecovery(null);
              }}
            >
              Keep my changes
            </button>
            <button
              className="button"
              onClick={() => {
                drafts.clear();
                setRecovery(null);
              }}
            >
              Use the saved version
            </button>
          </div>
        </div>
      )}
      {state.status === 'conflict' && (
        <div className="conflict" role="alert">
          <p>
            <span className="state">This text changed elsewhere</span> while you were editing.
          </p>
          <div className="row-actions">
            <button className="button" onClick={() => controller.current!.keepMine(state.currentRevisionId)}>
              Keep my version
            </button>
            <button
              className="button"
              onClick={async () => {
                const latest = await api.readText(ws, item.id);
                setValue(latest.text);
                controller.current!.discardMine(latest.revisionId);
              }}
            >
              Use the saved version
            </button>
          </div>
        </div>
      )}
      <textarea
        ref={area}
        className="textarea"
        aria-label="Text"
        style={{ minHeight: '50vh', fontFamily: 'var(--font-mono)' }}
        value={value}
        readOnly={!editable}
        onChange={(event) => {
          setValue(event.target.value);
          controller.current!.edit(event.target.value);
        }}
      />
    </div>
  );
}
